/**
 * Scene setup: sky, fog, lighting — all driven by palette.json.
 * Daylight rig: shadow-casting sun + hemisphere fill + ambient floor.
 * Fog range always covers the furthest rendered terrain chunk.
 */
import * as THREE from 'three';
import palette from './palette';

export interface SceneBudget {
  drawCalls: number;
  maxDrawCalls: number;
  textures: number;
  maxTextures: number;
}

export function createSceneBudget(maxDrawCalls = 200, maxTextures = 32): SceneBudget {
  return { drawCalls: 0, maxDrawCalls, textures: 0, maxTextures };
}

/** Hex string (with or without #) → THREE.Color. */
function hex(color: string): THREE.Color {
  return new THREE.Color(color.startsWith('#') ? color : '#' + color);
}

/** Furthest terrain distance, must match Terrain.ts VISIBILITY_RADIUS × SECTOR_SIZE + margin. */
export const VIEW_DISTANCE = 280;

export function buildScene(): { scene: THREE.Scene; budget: SceneBudget } {
  const scene = new THREE.Scene();
  const budget = createSceneBudget(200, 32);

  const skyColor = hex(palette.colors.sky.hex);
  scene.background = skyColor;
  // Fog starts past the play area, ends past the furthest chunk — no popping skyline.
  scene.fog = new THREE.Fog(skyColor, 120, VIEW_DISTANCE);

  // Sun — warm, shadow-casting, covers the 60 m play area around origin.
  const sun = new THREE.DirectionalLight(0xfff2d9, 3.0);
  sun.position.set(40, 60, 25);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  sun.shadow.camera.near = 10;
  sun.shadow.camera.far = 160;
  sun.shadow.camera.left = -45;
  sun.shadow.camera.right = 45;
  sun.shadow.camera.top = 45;
  sun.shadow.camera.bottom = -45;
  sun.shadow.bias = -0.0004;
  scene.add(sun);

  // Hemisphere fill — THIS is what fixes the darkness: sky color from above,
  // warm ground bounce from below, so shadowed faces are never black.
  const hemi = new THREE.HemisphereLight(skyColor, new THREE.Color('#8a7a5f'), 0.9);
  scene.add(hemi);

  // Small ambient floor so unlit crevices keep detail.
  const ambient = new THREE.AmbientLight(skyColor, 0.25);
  scene.add(ambient);

  return { scene, budget };
}

/** Track draw calls by wrapping scene.add. */
export function trackAdd(scene: THREE.Scene, obj: THREE.Object3D, budget: SceneBudget): void {
  scene.add(obj);
  obj.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) budget.drawCalls++;
  });
  if (budget.drawCalls > budget.maxDrawCalls) {
    console.warn(`draw-call budget exceeded: ${budget.drawCalls} / ${budget.maxDrawCalls}`);
  }
}
